import React, { useRef, useState } from 'react';

import clsx from 'clsx';
import { makeStyles, Theme, useTheme } from '@material-ui/core/styles';
import Drawer from '@material-ui/core/Drawer';
import {
  Fonts,
  IconButton,
  Menu,
  Typography,
  WebMenuItem,
  Tooltip,
  FleetaLogoOnly,
} from '@thingsw/pitta-design-system';
import { Link, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

import MoreHorizIcon from '@material-ui/icons/MoreHoriz';
import MenuOpenIcon from '@material-ui/icons/MenuOpen';

import { LangButton } from '../components/LangButton';
import { genTooltip } from '../utils';
import { WebviewerMenuList } from './WebviewerMenuList';
import { useEffect } from 'react';
import { PricingModal } from './modals/PricingModal';
import { LightColors, Languages } from '@thingsw/pitta-modules';
import DownLoadFleetaAppModal from './modals/DownLoadFleetaAppModal';

const drawerWidth = 235;

const useStyles = makeStyles((theme: Theme) => ({
  drawer: {
    width: drawerWidth,
    flexShrink: 0,
    whiteSpace: 'nowrap',
    display: 'flex',
  },
  drawerPaper: {
    backgroundColor: '#f8f8f8',
    overflow: 'visible',
  },
  drawerOpen: {
    width: drawerWidth,
    transition: theme.transitions.create('width', {
      easing: theme.transitions.easing.sharp,
      duration: theme.transitions.duration.enteringScreen,
    }),
  },
  drawerClose: {
    transition: theme.transitions.create('width', {
      easing: theme.transitions.easing.sharp,
      duration: theme.transitions.duration.leavingScreen,
    }),
    width: theme.spacing(7) + 1,
    [theme.breakpoints.up('sm')]: {
      width: theme.spacing(9) + 1,
    },
  },

  toolbar: {
    display: 'flex',
    alignItems: 'center',
    // necessary for content to be below app bar
    // ...theme.mixins.toolbar,
    minHeight: theme.spacing(7),
    marginBottom: theme.spacing(1),
  },
  toolbarOpen: {
    justifyContent: 'flex-start',
    padding: theme.spacing(0, 3),
  },
  toolbarClose: {
    padding: theme.spacing(0, 2.25),
  },
  vcenter: {
    display: 'flex',
    alignItems: 'center',
  },
  listItemText: {
    ...Fonts.Default.BodyBold,
  },
  listItemTextDivider: {
    marginBottom: 0,
    marginTop: 8,
  },
  langDivOpen: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    height: 80,
    borderBottom: `1px solid ${LightColors.primary['5']}`,

    ...(theme.direction === 'rtl' ? { marginRight: 2 } : { marginLeft: 2 }),
    paddingTop: 8,
  },
  langDivClose: {
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    borderBottom: `1px solid ${LightColors.primary['5']}`,
  },
  drawerDivOpen: {
    height: 55,
    display: 'flex',
    justifyContent: 'flex-end',
  },
  drawerDivClose: {
    height: 55,
    display: 'flex',
    justifyContent: 'center',
  },
  btnIconOpen:
    theme.direction === 'rtl' ? { marginLeft: -12 } : { marginRight: -12 },
  btnIconClose: {
    padding: theme.spacing(0.75, 1.5),
    marginBottom: 18,
  },
  menuOpenIcon:
    theme.direction === 'rtl'
      ? {
          transform: 'matrix(-1, 0, 0, 1, 0, 0)',
        }
      : {},
  menuCloseIcon:
    theme.direction === 'rtl'
      ? {}
      : {
          transform: 'matrix(-1, 0, 0, 1, 0, 0)',
        },
  upgradeBtn: {
    transition: theme.transitions.create('width'),
    whiteSpace: 'normal',
    wordBreak: 'break-all',
  },
  upgradeBtnClose: {
    minWidth: 50,
    maxWidth: 50,
    margin: '0 11px',
  },
  btnDivOpen: {
    padding: theme.spacing(0, 3),
  },
  langBtnDiv: {
    padding: theme.spacing(1, 0),
    // mantis-8443 langBtn 클릭 시 모달창 절반이 뒤로 숨겨지는 현상 수정을 위해 zIndex값 각주 처리 -SW-
    // zIndex: 99,
  },
  langBtnDivClose: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    height: 36,
    margin: '20px 0 12px 0',
  },
  langBtnClose: {
    height: theme.spacing(4),
    width: theme.spacing(4),
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
  },
  langBtnOpen: { marginBottom: 0 },

  divider: {
    margin: theme.spacing(1, 0),
  },
  appIcon: {
    fontSize: 20,
    color: LightColors.primary['1'],
    marginRight: theme.spacing(1),
  },
  tnpDiv: {
    ...(theme.direction === 'rtl' ? { paddingRight: 44 } : { paddingLeft: 44 }),
  },
  modalClose: {
    right: 32,
  },
  modalContent: { padding: 0 },
  webModalContent: { justifyContent: 'center' },
  appIconItem: {
    ...(theme.direction === 'rtl' && {
      display: 'flex',
      justifyContent: 'space-between',
      paddingRight: 42,
    }),
  },
}));

interface WebviewerMenuProps {
  open: boolean;
  onToggleMenu: () => void;
}

export const WebviewerMenu = ({ open, onToggleMenu }: WebviewerMenuProps) => {
  const classes = useStyles();
  const theme = useTheme() as Theme;
  const anchorRef = useRef<HTMLDivElement>(null);
  const appAnchorRef = useRef<HTMLButtonElement>(null);
  const { t, i18n } = useTranslation();
  const location = useLocation<{
    from: string;
  }>();

  const [openAppMenu, setOpenAppMenu] = useState(false);
  const [openPricing, setOpenPricing] = useState(false);
  const [openDownloadModal, setOpenDownloadModal] = useState(false);
  const [pathname, setPathname] = useState<string>();
  const [langOpenMenu, setLangOpenMenu] = useState(false);
  const [isShowLogo, setIsShowLogo] = useState(false);

  useEffect(() => {
    if (location.state?.from === 'login') {
      setTimeout(() => {
        setIsShowLogo(true);
      }, 10);
    }
  }, [location]);

  useEffect(() => {
    setPathname(location.pathname);
    if (pathname) {
      setOpenPricing(false);
    }
  }, [location.pathname, pathname]);

  return (
    <>
      <Drawer
        variant='permanent'
        className={clsx(classes.drawer, {
          [classes.drawerOpen]: open,
          [classes.drawerClose]: !open,
        })}
        classes={{
          paper: clsx(classes.drawerPaper, {
            [classes.drawerOpen]: open,
            [classes.drawerClose]: !open,
          }),
        }}
      >
        <div
          dir={theme.direction}
          className={clsx(classes.toolbar, {
            [classes.toolbarOpen]: open,
            [classes.toolbarClose]: !open,
          })}
        >
          {location.state?.from === 'login' ? (
            open ? (
              <Link
                to='/cameras'
                style={{
                  opacity: isShowLogo ? 1 : 0,
                  transition: 'opacity 1s ease-out',
                }}
                className={classes.vcenter}
              >
                <FleetaLogoOnly size='large' />
              </Link>
            ) : (
              <Link to='/cameras' className={classes.vcenter}>
                <svg
                  width='28'
                  height='28'
                  viewBox='0 0 28 28'
                  fill='none'
                  xmlns='http://www.w3.org/2000/svg'
                >
                  <path
                    d='M26.7366 24.721C26.797 24.898 26.8252 25.0027 26.8252 25.0308C26.8252 25.2965 26.6762 25.4293 26.3822 25.4293H23.9014C23.6638 25.4293 23.4866 25.3126 23.3698 25.0751L20.8447 19.8071H10.2129C10.0075 19.8071 9.90279 19.7025 9.90279 19.4973C9.90279 19.4087 9.91889 19.3363 9.94708 19.2759L10.9217 17.3281C11.0385 17.0343 11.26 16.8854 11.5862 16.8854H19.4272L14.0226 5.24278L4.36536 25.0751C4.24857 25.3126 4.07137 25.4293 3.83376 25.4293H1.61879C1.3248 25.4293 1.17578 25.2965 1.17578 25.0308C1.17578 25.0027 1.20398 24.898 1.26439 24.721L11.7634 3.42776C11.8802 3.19032 12.0493 2.99313 12.2708 2.82813C12.4923 2.66715 12.7218 2.58667 12.9594 2.58667H15.0415C15.2791 2.58667 15.5087 2.66715 15.7302 2.82813C15.9517 2.9891 16.1208 3.19032 16.2416 3.42776L26.7406 24.721H26.7366Z'
                    fill='#61007D'
                  />
                </svg>
              </Link>
            )
          ) : open ? (
            <Link to='/cameras' className={classes.vcenter}>
              <FleetaLogoOnly size='large' />
            </Link>
          ) : (
            <Link to='/cameras' className={classes.vcenter}>
              <svg
                width='28'
                height='28'
                viewBox='0 0 28 28'
                fill='none'
                xmlns='http://www.w3.org/2000/svg'
              >
                <path
                  d='M26.7366 24.721C26.797 24.898 26.8252 25.0027 26.8252 25.0308C26.8252 25.2965 26.6762 25.4293 26.3822 25.4293H23.9014C23.6638 25.4293 23.4866 25.3126 23.3698 25.0751L20.8447 19.8071H10.2129C10.0075 19.8071 9.90279 19.7025 9.90279 19.4973C9.90279 19.4087 9.91889 19.3363 9.94708 19.2759L10.9217 17.3281C11.0385 17.0343 11.26 16.8854 11.5862 16.8854H19.4272L14.0226 5.24278L4.36536 25.0751C4.24857 25.3126 4.07137 25.4293 3.83376 25.4293H1.61879C1.3248 25.4293 1.17578 25.2965 1.17578 25.0308C1.17578 25.0027 1.20398 24.898 1.26439 24.721L11.7634 3.42776C11.8802 3.19032 12.0493 2.99313 12.2708 2.82813C12.4923 2.66715 12.7218 2.58667 12.9594 2.58667H15.0415C15.2791 2.58667 15.5087 2.66715 15.7302 2.82813C15.9517 2.9891 16.1208 3.19032 16.2416 3.42776L26.7406 24.721H26.7366Z'
                  fill='#61007D'
                />
              </svg>
            </Link>
          )}
        </div>
        <WebviewerMenuList open={open} onClose={() => undefined} />
        <div
          className={clsx({
            [classes.btnDivOpen]: open,
          })}
          style={{ overflow: 'hidden' }}
        >
          <div
            dir={theme.direction}
            className={clsx({
              [classes.langDivOpen]: open,
              [classes.langDivClose]: !open,
            })}
          >
            <div
              ref={anchorRef}
              className={clsx(classes.langBtnDiv, {
                [classes.langBtnDivClose]: !open,
              })}
              onClick={() => setLangOpenMenu((o) => !o)}
            >
              {genTooltip(
                <LangButton
                  mode='webviewer'
                  // placement="top-start"
                  modifiers={{
                    offset: {
                      enabled: true,
                      offset: theme.direction === 'rtl' ? -8 : 8,
                    },
                    preventOverflow: {
                      enabled: false,
                    },
                    flip: {
                      enabled: false,
                    },
                  }}
                  openMenu={langOpenMenu}
                  onClose={() => setLangOpenMenu(false)}
                  anchorRef={anchorRef}
                  placement={'top-start'}
                  iconOnly={!open}
                  className={clsx({
                    [classes.langBtnOpen]: open,
                    [classes.langBtnClose]: !open,
                  })}
                />,
                Languages[i18n.language],
                open,
                undefined,
                langOpenMenu
              )}
            </div>
            <Tooltip
              title={
                <Typography category='Default' variant='Caption'>
                  {t('Apps and more...')}
                </Typography>
              }
              placement='right'
            >
              <IconButton
                ref={appAnchorRef}
                className={clsx({
                  [classes.btnIconOpen]: open,
                  [classes.btnIconClose]: !open,
                })}
                onClick={() => setOpenAppMenu(true)}
              >
                <MoreHorizIcon />
              </IconButton>
            </Tooltip>
          </div>
          <div
            dir={theme.direction}
            className={clsx({
              [classes.drawerDivOpen]: open,
              [classes.drawerDivClose]: !open,
            })}
          >
            {open ? (
              <Tooltip
                title={
                  <Typography category='Default' variant='Caption'>
                    {t('Collapse')}
                  </Typography>
                }
                placement='right'
              >
                <IconButton
                  color='secondary'
                  className={clsx({ [classes.btnIconOpen]: open })}
                  onClick={onToggleMenu}
                >
                  <MenuOpenIcon
                    className={clsx({
                      [classes.menuOpenIcon]: open,
                      [classes.menuCloseIcon]: !open,
                    })}
                  />
                </IconButton>
              </Tooltip>
            ) : (
              <Tooltip
                title={
                  <Typography
                    category='Default'
                    variant='Caption'
                    htmlColor={LightColors.primary['5']}
                  >
                    {t('Expand')}
                  </Typography>
                }
                placement='right'
              >
                <IconButton
                  color='secondary'
                  className={clsx({ [classes.btnIconOpen]: open })}
                  onClick={onToggleMenu}
                >
                  <MenuOpenIcon
                    className={clsx({
                      [classes.menuOpenIcon]: open,
                      [classes.menuCloseIcon]: !open,
                    })}
                  />
                </IconButton>
              </Tooltip>
            )}
          </div>
        </div>
        <div style={{ zIndex: 99 }}>
          <Menu
            open={openAppMenu}
            anchorEl={appAnchorRef?.current}
            onClickAway={() => {
              setOpenAppMenu(false);
            }}
            placement='top-start'
            modifiers={{ preventOverflow: { enabled: true } }}
          >
            {/* <WebMenuItem
              appIcon
              className={classes.appIconItem}
              startIcon={<AppleIcon className={classes.appIcon} />}
              onClick={() => {
                setOpenAppMenu(false);
                setOpenDownloadModal(true);
              }}
            >
              {t("iOS")}
            </WebMenuItem>
            <WebMenuItem
              appIcon
              className={classes.appIconItem}
              startIcon={<GooglePlay className={classes.appIcon} />}
              onClick={() => {
                setOpenAppMenu(false);
                setOpenDownloadModal(true);
              }}
            >
              {t("Android")}
            </WebMenuItem> */}
            <WebMenuItem
              appIcon
              className={classes.appIconItem}
              onClick={() => {
                setOpenAppMenu(false);
                setOpenDownloadModal(true);
              }}
            >
              {t('App Download')}
            </WebMenuItem>

            <WebMenuItem
              onClick={() => {
                setOpenAppMenu(false);
                window.open(
                  // 한국어 / 일본어 일시 해당 국가의 Terms & Policy로 이동 (KimSH)
                  i18n.language === "ko"
                    ? "https://blackvue.com/terms-conditions-korean-page/"
                    : i18n.language === "ja"
                    ? "https://blackvue.com/terms-conditions-japan/"
                    : "https://blackvue.com/terms-conditions-cloud-terms-of-service/",
                  '_blank'
                );
              }}
            >
              {t('Terms & Policies')}
            </WebMenuItem>
          </Menu>
        </div>
      </Drawer>
      <PricingModal open={openPricing} onClose={() => setOpenPricing(false)} />
      <DownLoadFleetaAppModal
        open={openDownloadModal}
        setOpenDownloadModal={setOpenDownloadModal}
      />
    </>
  );
};
